import type { ColorScheme } from "@/hooks/useColorScheme";
import { getLocalizedDateFormats } from "@/overlay/components/CommonParts/PlotHighchart/utils/chart-options/axis-default";
import { CHART_BACKGROUND_COLOR } from "@/overlay/components/CommonParts/PlotHighchart/utils/chart-options/chartOptions";
import {
  type PlotlineProps,
  createHighchartsPlotLines,
} from "@/overlay/components/CommonParts/PlotHighchart/utils/chart-options/plotline";
import type { DatetimesWithOffset } from "@/utility/time";
import { getTimeRangeWithOffset } from "@/utility/time";
import type { I18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { DateTime, Interval } from "luxon";
import { ENERGY_VALUE_DECIMALS } from "./const";
// Generator for creating array of days in interval
function* days(interval: Interval) {
  let cursor = interval.start?.startOf("day") || DateTime.utc().startOf("day");
  while (cursor < (interval.end || DateTime.utc().startOf("day"))) {
    yield cursor;
    cursor = cursor.plus({ days: 1 });
  }
}

export function getWeekendPlotBands(data?: any[][]) {
  if (!data || !data.length) {
    return [];
  }

  const weekendPlotBands = [];

  const [startMillis] = data[0];
  const [endMillis] = data[data.length - 1];
  const startDate = DateTime.fromMillis(startMillis).toUTC().startOf("day");
  const endDate = DateTime.fromMillis(endMillis).toUTC().startOf("day");

  const interval = Interval.fromDateTimes(startDate, endDate);

  for (const d of days(interval)) {
    // if date is in weekend
    if (d.weekday === 6 || d.weekday === 7) {
      weekendPlotBands.push({
        color: "#EEE",
        from: d.valueOf(),
        to: d.endOf("day").valueOf(),
      });
    }
  }

  return weekendPlotBands;
}

type DisplaytTimeRange = { start: DateTime; end: DateTime };

export function getDisplayTimeRange(
  datetimes: DatetimesWithOffset,
  displayTime: string,
): DisplaytTimeRange | undefined {
  const timeRange = getTimeRangeWithOffset(displayTime, datetimes);

  if (!timeRange || !timeRange.start || !timeRange.end) {
    return undefined;
  }

  return {
    start: DateTime.fromISO(timeRange.start),
    end: DateTime.fromISO(timeRange.end),
  };
}

export function getEnergyChartConfiguration(
  plotlinePropsList: PlotlineProps[],
  i18n: I18n,
  data?: number[][],
  displayRange?: DisplaytTimeRange,
  colorScheme: ColorScheme = "light",
) {
  const plotBands = getWeekendPlotBands(data);
  const plotLines = plotlinePropsList
    .flatMap((plotlineProp) => {
      return createHighchartsPlotLines(plotlineProp);
    })
    .flat();
  return {
    accessibility: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    palette: { colorScheme },
    boost: { seriesThreshold: 18 },
    xAxis: [
      {
        // Bottom X axis
        type: "datetime",
        dateTimeLabelFormats: getLocalizedDateFormats(i18n.locale),
        tickInterval: 24 * 3600 * 1000, // one day
        gridLineWidth: 1,
        minorTickInterval: 6 * 3600 * 1000, // 6 hours
        minorGridLineWidth: 0,
        minorTickLength: 5,
        minorTickWidth: 1,

        plotBands: [...plotBands],

        min: displayRange?.start.toMillis(),
        max: displayRange?.end.toMillis(),

        plotLines: plotLines,
      },
      {
        // Top X axis
        linkedTo: 0,
        type: "datetime",
        tickInterval: 24 * 3600 * 1000, // one day
        labels: {
          format: "{value:%a}",
          align: "left",
          x: 3,
          y: -5,
        },
        opposite: true,
        tickLength: 20,
        gridLineWidth: 1,
      },
    ],
    chart: {
      zooming: { type: "xy" },
      allowMutatingData: false,
      backgroundColor: CHART_BACKGROUND_COLOR,
    },
  };
}
/**
 * Human-readable labels for site IDs used in the UI.
 *
 * Keep this as the single source of truth for both legacy and new backend IDs.
 */
const siteTranslationMap: Record<string, () => string> = {
  de: () => t`Germany`,
  "germany-national-wind_01": () => t`Germany`,
  "germany-national-solar_01": () => t`Germany`,
  fr: () => t`France`,
  "france-national-wind_01": () => t`France`,
  "france-national-solar_01": () => t`France`,
  ch: () => t`Switzerland`,
  "switzerland-national-wind_01": () => t`Switzerland`,
  "switzerland-national-solar_01": () => t`Switzerland`,
  it: () => t`Italy`,
  "italy-national-wind_01": () => t`Italy`,
  "italy-national-solar_01": () => t`Italy`,
  uk: () => t`UK`,
  "united-kingdom-national-wind_01": () => t`UK`,
  "united-kingdom-national-solar_01": () => t`UK`,
  es: () => t`Spain`,
  "spain-national-wind_01": () => t`Spain`,
  "spain-national-solar_01": () => t`Spain`,
  nl: () => t`Netherlands`,
  "netherlands-national-wind_01": () => t`Netherlands`,
  "netherlands-national-solar_01": () => t`Netherlands`,
  be: () => t`Belgium`,
  "belgium-national-wind_01": () => t`Belgium`,
  "belgium-national-solar_01": () => t`Belgium`,
  "at-apg": () => t`Austria`,
  "austria-national-wind_01": () => t`Austria`,
  "austria-national-solar_01": () => t`Austria`,
  dk: () => t`Denmark`,
  "denmark-national-wind_01": () => t`Denmark`,
  "denmark-national-solar_01": () => t`Denmark`,
  pl: () => t`Poland`,
  "poland-national-wind_01": () => t`Poland`,
  "poland-national-solar_01": () => t`Poland`,
  hu: () => t`Hungary`,
  "hungary-national-wind_01": () => t`Hungary`,
  "hungary-national-solar_01": () => t`Hungary`,
  cz: () => t`Czechia`,
  "czechia-national-wind_01": () => t`Czechia`,
  "czechia-national-solar_01": () => t`Czechia`,
  sk: () => t`Slovakia`,
  "slovakia-national-wind_01": () => t`Slovakia`,
  "slovakia-national-solar_01": () => t`Slovakia`,
  "de-transnetbw": () => "TransnetBW",
  "germany-national-wind_transnetbw": () => "TransnetBW",
  "germany-national-solar_transnetbw": () => "TransnetBW",
  "de-tennet": () => "Tennet",
  "germany-national-wind_tennet": () => "Tennet",
  "germany-national-solar_tennet": () => "Tennet",
  "de-amprion": () => "Amprion",
  "germany-national-wind_amprion": () => "Amprion",
  "germany-national-solar_amprion": () => "Amprion",
  "de-50hertz": () => "50Hertz",
  "germany-national-wind_50hertz": () => "50Hertz",
  "germany-national-solar_50hertz": () => "50Hertz",
  "us-erco_uncurtailed": () => "US-ERCOT",
  "united-states-national-wind_ercot_uncurtailed": () => "US-ERCOT",
  "united-states-national-wind_ercot": () => "US-ERCOT",
  "united-states-national-wind_ercot_coastal_uncurtailed": () => "Coastal",
  "united-states-national-wind_ercot_north_uncurtailed": () => "North",
  "united-states-national-wind_ercot_panhandle_uncurtailed": () => "Panhandle",
  "united-states-national-wind_ercot_south_uncurtailed": () => "South",
  "united-states-national-wind_ercot_west_uncurtailed": () => "West",
  "united-states-national-wind_ercot_wakes_uncurtailed": () => "US-ERCOT",
  "united-states-national-solar_ercot_uncurtailed_capacity_corrected": () => "US-ERCOT",
  "united-states-national-solar_ercot_centereast_uncurtailed_capacity_corrected": () => "Center East",
  "united-states-national-solar_ercot_centerwest_uncurtailed_capacity_corrected": () => "Center West",
  "united-states-national-solar_ercot_fareast_uncurtailed_capacity_corrected": () => "Far East",
  "united-states-national-solar_ercot_farwest_uncurtailed_capacity_corrected": () => "Far West",
  "united-states-national-solar_ercot_northwest_uncurtailed_capacity_corrected": () => "Northwest",
  "united-states-national-solar_ercot_southeast_uncurtailed_capacity_corrected": () => "Southeast",
  "united-states-national-solar_ercot": () => "US-ERCOT",
  "us-erco": () => "US-ERCOT",
  "us-erco_coast": () => "US-ERCOT",
  "us-erco_north": () => "US-ERCOT",
  "us-erco_panhandle": () => "US-ERCOT",
  "us-erco_south": () => "US-ERCOT",
  "us-erco_west": () => "US-ERCOT",
  "us-erco_wakes": () => "US-ERCOT",
};

/**
 * Migration map for wind forecasts:
 * legacy/internal site IDs -> new unique backend site IDs.
 */
const siteIdToUniqueWindMap: Record<string, string> = {
  "at-apg": "austria-national-wind_01",
  de: "germany-national-wind_01",
  "de-transnetbw": "germany-national-wind_transnetbw",
  "de-50hertz": "germany-national-wind_50hertz",
  "de-tennet": "germany-national-wind_tennet",
  "de-amprion": "germany-national-wind_amprion",
  be: "belgium-national-wind_01",
  dk: "denmark-national-wind_01",
  fr: "france-national-wind_01",
  it: "italy-national-wind_01",
  es: "spain-national-wind_01",
  ch: "switzerland-national-wind_01",
  nl: "netherlands-national-wind_01",
  uk: "united-kingdom-national-wind_01",
  pl: "poland-national-wind_01",
  hu: "hungary-national-wind_01",
  cz: "czechia-national-wind_01",
  sk: "slovakia-national-wind_01",
  "us-erco_uncurtailed": "united-states-national-wind_ercot_uncurtailed",
  "us-erco": "united-states-national-wind_ercot",
  "us-erco_coast": "united-states-national-wind_ercot_coast",
  "us-erco_north": "united-states-national-wind_ercot_north",
  "us-erco_panhandle": "united-states-national-wind_ercot_panhandle",
  "us-erco_south": "united-states-national-wind_ercot_south",
  "us-erco_west": "united-states-national-wind_ercot_west",
  "us-erco_wakes": "united-states-national-wind_ercot_wakes",
};

/**
 * Migration map for solar forecasts:
 * legacy/internal site IDs -> new unique backend site IDs.
 */
const siteIdToUniqueSolarMap: Record<string, string> = {
  "at-apg": "austria-national-solar_01",
  de: "germany-national-solar_01",
  "de-transnetbw": "germany-national-solar_transnetbw",
  "de-50hertz": "germany-national-solar_50hertz",
  "de-tennet": "germany-national-solar_tennet",
  "de-amprion": "germany-national-solar_amprion",
  be: "belgium-national-solar_01",
  dk: "denmark-national-solar_01",
  fr: "france-national-solar_01",
  it: "italy-national-solar_01",
  es: "spain-national-solar_01",
  ch: "switzerland-national-solar_01",
  nl: "netherlands-national-solar_01",
  uk: "united-kingdom-national-solar_01",
  pl: "poland-national-solar_01",
  hu: "hungary-national-solar_01",
  cz: "czechia-national-solar_01",
  sk: "slovakia-national-solar_01",
  "us-erco_uncurtailed": "united-states-national-solar_ercot_uncurtailed_capacity_corrected",
  "us-erco": "united-states-national-solar_ercot",
  "us-erco_centereast": "united-states-national-solar_ercot_centereast_capacity_corrected",
  "us-erco_centerwest": "united-states-national-solar_ercot_centerwest_capacity_corrected",
  "us-erco_fareast": "united-states-national-solar_ercot_fareast_capacity_corrected",
  "us-erco_farwest": "united-states-national-solar_ercot_farwest_capacity_corrected",
  "us-erco_northwest": "united-states-national-solar_ercot_northwest_capacity_corrected",
  "us-erco_southeast": "united-states-national-solar_ercot_southeast_capacity_corrected",
};

/**
 * Forecast-type specific migration maps used when canonicalizing plot state
 * after loading legacy entries from DB.
 */
// TODO: cleanup
const legacySiteIdToUniqueSiteIdMapByForecastType: Record<string, Record<string, string>> = {
  wlp: siteIdToUniqueWindMap,
  slp: siteIdToUniqueSolarMap,
};

// TODO: cleanup
function invertSiteMap(siteMap: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(siteMap).map(([legacySiteId, uniqueSiteId]) => [uniqueSiteId, legacySiteId]),
  );
}

// TODO: cleanup
const uniqueSiteIdToLegacySiteIdMapByForecastType: Record<string, Record<string, string>> = {
  wlp: invertSiteMap(siteIdToUniqueWindMap),
  slp: invertSiteMap(siteIdToUniqueSolarMap),
};

/**
 * Converts one legacy/internal site ID to the new unique backend site ID.
 *
 * This is intentionally idempotent: if `siteId` is already a new ID (or unknown),
 * the input value is returned unchanged.
 */
// TODO: cleanup
export function normalizeSiteIdForEnergyApi(siteId: string, forecastType: string): string {
  const siteMap = legacySiteIdToUniqueSiteIdMapByForecastType[forecastType];
  return siteMap?.[siteId] ?? siteId;
}

export function remapSiteIdForForecastTypeChange(
  siteId: string,
  previousForecastType: string,
  nextForecastType: string,
): string {
  const previousSiteMap = uniqueSiteIdToLegacySiteIdMapByForecastType[previousForecastType];
  const nextSiteMap = legacySiteIdToUniqueSiteIdMapByForecastType[nextForecastType];

  if (!previousSiteMap || !nextSiteMap) {
    return siteId;
  }

  const legacySiteId = previousSiteMap[siteId] ?? siteId;
  return nextSiteMap[legacySiteId] ?? siteId;
}

/**
 * Vectorized variant of `normalizeSiteIdForEnergyApi`.
 *
 * It also de-duplicates resulting IDs because multiple legacy IDs can collapse
 * to the same canonical backend ID.
 */
export function normalizeSiteIdsForEnergyApi(
  siteIds: string[] | undefined,
  forecastType: string,
): string[] | undefined {
  if (!siteIds) {
    return siteIds;
  }

  return [...new Set(siteIds.map((siteId) => normalizeSiteIdForEnergyApi(siteId, forecastType)))];
}

/**
 * Produces a localized site label for both legacy and new backend site IDs.
 */
export function generateSiteLabel(siteId: string) {
  if (siteId in siteTranslationMap) {
    return siteTranslationMap[siteId]();
  }

  // Return the original site as fallback
  return siteId;
}

const forecastTranslationMap: { [type: string]: () => string } = {
  wlp: () => t`Wind power`,
  slp: () => t`Solar power`,
};

export function generateEnergyForecastTypeLabel(forecastType: string) {
  if (forecastType in forecastTranslationMap) {
    return forecastTranslationMap[forecastType]();
  }
  // Return the original site as fallback
  return forecastType;
}

// Workaround for frontend translation of dynamic data
// In future this will be handled in backend as data are dynamic and may change
export const generateHumanReadableTitle = (forecastType: string, siteId: string, model: string, val_unit: string) =>
  `${generateEnergyForecastTypeLabel(forecastType)} ${generateSiteLabel(siteId)} (${model}) [${val_unit}]`;

export function generateHumanReadableCaptions(forecastType?: string) {
  if (!forecastType) {
    return "";
  }
  return forecastType === "slp" ? t`Solar power` : t`Wind power`;
}

/**
 * Scales the numeric values of Highcharts series from the API's native MW unit to
 * the selected display unit (e.g. MW -> GW = 0.001), preserving the leading
 * timestamp of each data point and scaling every value component (handles both
 * `[x, y]` and `[x, low, high]` range points). Scaled values are rounded to
 * `ENERGY_VALUE_DECIMALS` decimals.
 */
export function scaleEnergySeries<T extends { data?: any }>(series: T[], factor: number): T[] {
  return series.map((s) => ({
    ...s,
    data: (s.data ?? []).map((point: Array<number | null>) =>
      // Preserve the timestamp (index 0) and null gaps; only scale real values
      // (null * factor === 0 in JS, which would render missing data as zero).
      point.map((value, index) =>
        index === 0 || value == null ? value : Number((value * factor).toFixed(ENERGY_VALUE_DECIMALS)),
      ),
    ),
  }));
}

const ENERGY_MODEL_PRIORITY = ["mm-euro1k", "mix"];

export function resolvePreferredEnergyModel(availableModelIds: string[], currentModel?: string) {
  if (currentModel && availableModelIds.includes(currentModel)) {
    return currentModel;
  }

  for (const preferredModel of ENERGY_MODEL_PRIORITY) {
    if (availableModelIds.includes(preferredModel)) {
      return preferredModel;
    }
  }

  return availableModelIds[0] || currentModel || "";
}
